import {
  IDescriptionsResort, IResortPathInfo, ITrailMap, ResortOverviewTab,
} from 'api/resort/types';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { ILightboxImage, Lightbox } from 'components/common/Lightbox';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useAnalytics } from 'util/analytics';
import { Description } from 'components/resort/ResortDescriptionTabs/Description';
import { ReactComponent as SvgSearch } from 'public/assets/icons/search.svg';

import { useIsMobile } from 'util/useIsMobile';
import { useBrandfolderOptimizedImage } from 'util/useBrandfolderOptimizedImage';
import styles from './styles.module.scss';

export type IProps = {
  isFresh: boolean;
  trailMap: ITrailMap;
  resort: IResortPathInfo;
  descriptionTabs?: Array<IDescriptionsResort>;
};

export const TrailMap = ({
  trailMap,
  resort,
  descriptionTabs,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();
  const isMobile = useIsMobile();
  const optimizedImage = useBrandfolderOptimizedImage(trailMap.image, 2048);
  const optimizedThumbnail = useBrandfolderOptimizedImage(trailMap.thumbnail);
  const { liveUrl } = trailMap;
  const isLiveUrl = Boolean(liveUrl && !liveUrl.toLowerCase().endsWith('.jpg'));

  const images: Array<ILightboxImage> = [{
    title: `${resort.title}`,
    alt: `${resort.title}`,
    src: optimizedImage ?? '',
  }];

  const terrainTab = useMemo(() => descriptionTabs?.find((ot) => ot.tab === ResortOverviewTab.terrain), [descriptionTabs]);

  const [lightBoxIndex, setLightBoxOpen] = useState<boolean>(false);

  const openImage = useCallback((e: React.SyntheticEvent) => {
    if (isMobile) { return; }

    e.preventDefault();
    setLightBoxOpen(true);
    a.expandImage({ content: 'Trail Map' });
  }, [images]);

  return (
    <BoxWithShadow>
      <BlockHeader title={t('resort.trailMap.seoTitle', { resort: resort.title })} />
      <BlockContent>
        {t('resort.trailMap.seoText', { resort: resort.title })}
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        {trailMap.image && trailMap.thumbnail && (
        <div className={styles.trailMapContainer}>
          <a
            href={optimizedImage}
            onClick={openImage}
            className={styles.trailMapButton}
            tabIndex={-1}
            target="_blank"
            rel="noreferrer nofollow"
          >
            <img
              src={optimizedThumbnail}
              alt={t('resort.trailMap.seoTitle', { resort: resort.title })}
              className={styles.image}
            />
            <span className={styles.expandBlockCaption}>{t('resort.trailMap.expand')}</span>
            <span className={styles.expandBlockIcon}><SvgSearch /></span>
          </a>
        </div>
        )}
        {isLiveUrl && (
          <div className={styles.embedContainer}>
            <iframe
              src={liveUrl ?? undefined}
              title={`${resort.title} trail map`}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
        {terrainTab && (
          <Description
            text={terrainTab.description}
            isActive
            title=""
            tab={ResortOverviewTab.terrain}
          />
        )}
      </BlockContent>
      {lightBoxIndex && (
        <Lightbox onClose={() => setLightBoxOpen(false)} images={images} index={0} />
      )}
    </BoxWithShadow>
  );
};
